import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import {
  Library,
  Inbox,
  FolderOpen,
  Filter,
  Users,
  ClipboardCheck,
  Settings,
  LogOut,
  HardDrive,
  Menu,
  X,
} from "lucide-react";

export function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { path: "/library", label: "Library", icon: Library },
    { path: "/arrivals", label: "Arrivals", icon: Inbox },
    { path: "/collections", label: "Collections", icon: FolderOpen },
    { path: "/smart-folders", label: "Smart Folders", icon: Filter },
    { path: "/senders", label: "Senders", icon: Users },
    { path: "/review", label: "Review", icon: ClipboardCheck },
    { path: "/settings", label: "Settings", icon: Settings },
  ];

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="flex h-screen bg-surface-1 dark:bg-dark-surface-0">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
          onClick={closeMobile}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-64 border-r border-border-default bg-surface-0 dark:border-dark-border-default dark:bg-dark-surface-1 flex flex-col shadow-lg transition-transform duration-medium md:translate-x-0 md:shadow-none ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Brand */}
        <div className="px-5 py-4 border-b border-border-default dark:border-dark-border-default flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent text-white">
              <HardDrive size={18} strokeWidth={2.5} />
            </div>
            <h1 className="text-base font-bold tracking-tight text-primary dark:text-white">
              AttachDrive
            </h1>
          </div>
          <button onClick={closeMobile} className="md:hidden p-1 rounded-md text-text-muted hover:text-text-primary dark:text-dark-text-muted dark:hover:text-dark-text-primary">
            <X size={20} />
          </button>
        </div>

        {user?.email && (
          <p className="px-5 py-2 text-xs text-text-muted dark:text-dark-text-muted truncate border-b border-border-default dark:border-dark-border-default">
            {user.email}
          </p>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMobile}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-fast ${
                  isActive
                    ? "bg-accent-50 text-accent-600 dark:bg-accent-500/10 dark:text-accent-400"
                    : "text-text-secondary hover:bg-surface-2 hover:text-text-primary dark:text-dark-text-secondary dark:hover:bg-dark-surface-2 dark:hover:text-dark-text-primary"
                }`}
              >
                <Icon size={18} strokeWidth={isActive ? 2.5 : 2} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-border-default dark:border-dark-border-default">
          <button
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-2 hover:text-text-primary dark:text-dark-text-secondary dark:hover:bg-dark-surface-2 dark:hover:text-dark-text-primary transition-colors duration-fast"
          >
            <LogOut size={18} strokeWidth={2} />
            Log out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto min-w-0">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-30 border-b border-border-default bg-surface-0/90 backdrop-blur-md dark:border-dark-border-default dark:bg-dark-surface-1/90 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-1.5 rounded-lg text-text-secondary hover:bg-surface-2 dark:text-dark-text-secondary dark:hover:bg-dark-surface-2"
          >
            <Menu size={20} />
          </button>
          <h2 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">
            {navItems.find((n) => n.path === location.pathname)?.label || "AttachDrive"}
          </h2>
        </header>

        {/* Desktop header */}
        <header className="hidden md:block sticky top-0 z-10 border-b border-border-default bg-surface-0/80 backdrop-blur-md dark:border-dark-border-default dark:bg-dark-surface-1/80 px-8 py-3.5">
          <h2 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary tracking-tight">
            {navItems.find((n) => n.path === location.pathname)?.label || "AttachDrive"}
          </h2>
        </header>

        {/* Page content */}
        <div className="p-4 md:p-8">{children}</div>
      </main>
    </div>
  );
}
